import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Card, CardContent, Grid, Button, TextField, Alert, Chip, LinearProgress } from '@mui/material';
import { styled, keyframes } from '@mui/system';

const dataFlow = keyframes`
  0% {
    transform: translateY(0) scale(1);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-20px) scale(1.2);
    opacity: 1;
  }
  100% {
    transform: translateY(-40px) scale(0.8);
    opacity: 0;
  }
`;

const timeWarp = keyframes`
  0% {
    transform: rotate(0deg) scale(1);
    filter: hue-rotate(0deg);
  }
  25% {
    transform: rotate(90deg) scale(1.1);
    filter: hue-rotate(90deg);
  }
  50% {
    transform: rotate(180deg) scale(1.2);
    filter: hue-rotate(180deg);
  }
  75% {
    transform: rotate(270deg) scale(1.1);
    filter: hue-rotate(270deg);
  }
  100% {
    transform: rotate(360deg) scale(1);
    filter: hue-rotate(360deg);
  }
`;

const TemporalField = styled(Box)`
  position: relative;
  height: 300px;
  background: linear-gradient(45deg, #000814 0%, #001d3d 50%, #003566 100%);
  border: 2px solid #00f5ff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 200px;
    height: 200px;
    transform: translate(-50%, -50%);
    background: conic-gradient(from 0deg, transparent, #00f5ff, transparent, #ff00ff, transparent);
    border-radius: 50%;
    animation: ${timeWarp} 3s linear infinite;
    opacity: 0.3;
  }
`;

const DataPacket = styled(Box)<{ delay: number }>`
  position: absolute;
  width: 8px;
  height: 8px;
  background: #00ff41;
  border-radius: 50%;
  box-shadow: 0 0 15px #00ff41;
  animation: ${dataFlow} 2s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
`;

const BitstreamDisplay = styled(Box)`
  font-family: 'Courier New', monospace;
  background: #000;
  border: 1px solid #00ff41;
  border-radius: 4px;
  padding: 10px;
  height: 100px;
  overflow-y: auto;
  color: #00ff41;
  font-size: 12px;
  line-height: 1.2;
`;

interface TimeModemData {
  timestamp: number;
  bit: number;
  source: 'future' | 'present';
  confidence: number;
}

interface TimeModemProps {
  quantumData: { timestamp: number; pattern: 'wave' | 'particle'; bit: number }[];
}

export const TimeModem: React.FC<TimeModemProps> = ({ quantumData }) => {
  const [isActive, setIsActive] = useState(false);
  const [messageToSend, setMessageToSend] = useState('');
  const [receivedBits, setReceivedBits] = useState<TimeModemData[]>([]);
  const [decodedMessage, setDecodedMessage] = useState('');
  const [temporalStability, setTemporalStability] = useState(85);
  const [anomalyDetected, setAnomalyDetected] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (isActive) {
      intervalRef.current = setInterval(() => {
        // Simulate receiving data from quantum experiment
        if (quantumData.length > 0) {
          const latestData = quantumData[quantumData.length - 1];
          
          // Simulate temporal processing
          const newBit: TimeModemData = {
            timestamp: Date.now(),
            bit: latestData.bit,
            source: Math.random() > 0.7 ? 'future' : 'present',
            confidence: 0.6 + Math.random() * 0.4
          };

          setReceivedBits(prev => [...prev.slice(-19), newBit]);

          // Update temporal stability
          setTemporalStability(prev => {
            const delta = (Math.random() - 0.5) * 10;
            return Math.max(20, Math.min(100, prev + delta));
          });

          // Check for anomalies
          if (newBit.source === 'future' && newBit.confidence > 0.9) {
            setAnomalyDetected(true);
            setTimeout(() => setAnomalyDetected(false), 2000);
          }
        }
      }, 500);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, quantumData]);

  useEffect(() => {
    // Decode message from received bits
    if (receivedBits.length >= 8) {
      const lastByte = receivedBits.slice(-8).map(b => b.bit).join('');
      const charCode = parseInt(lastByte, 2);
      if (charCode >= 32 && charCode <= 126) {
        const char = String.fromCharCode(charCode);
        setDecodedMessage(prev => prev + char);
      }
    }
  }, [receivedBits]);

  const handleSendMessage = () => {
    if (!messageToSend.trim()) return;

    // Convert message to binary
    const binaryMessage = messageToSend
      .split('')
      .map(char => char.charCodeAt(0).toString(2).padStart(8, '0'))
      .join('');

    // Simulate sending to future (in reality, this would encode quantum states)
    console.log('Encoding message for future transmission:', binaryMessage);
    
    setMessageToSend('');
  };

  const formatBitstream = () => {
    return receivedBits
      .slice(-100)
      .map((data, i) => 
        `${String(i).padStart(3, '0')}: ${data.bit} [${data.source}] (${(data.confidence * 100).toFixed(1)}%)`
      )
      .join('\n');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: '#00f5ff', textAlign: 'center' }}>
        Theoretical 1-Bit Time Modem
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ background: 'linear-gradient(135deg, #000814 0%, #001d3d 100%)', border: '1px solid #00f5ff', mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#00f5ff', mb: 2 }}>
                Temporal Communication Interface
              </Typography>

              <TemporalField>
                {isActive && Array.from({ length: 12 }).map((_, i) => (
                  <DataPacket key={i} delay={i * 0.2} />
                ))}
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: '#fff', 
                    position: 'relative', 
                    zIndex: 2,
                    textShadow: '0 0 10px #00f5ff'
                  }}
                >
                  {isActive ? 'TEMPORAL LINK ACTIVE' : 'TEMPORAL LINK STANDBY'}
                </Typography>
              </TemporalField>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => setIsActive(!isActive)}
                  sx={{
                    background: isActive ? 'linear-gradient(45deg, #ff0040, #ff4000)' : 'linear-gradient(45deg, #00f5ff, #0077ff)',
                    color: '#000',
                    fontWeight: 'bold'
                  }}
                >
                  {isActive ? 'DISCONNECT' : 'INITIATE LINK'}
                </Button>

                <Chip
                  label={`Stability: ${temporalStability.toFixed(1)}%`}
                  sx={{
                    background: temporalStability > 70 ? '#00ff41' : temporalStability > 40 ? '#ff9800' : '#ff0040',
                    color: '#000',
                    fontWeight: 'bold'
                  }}
                />
              </Box>

              <LinearProgress
                variant="determinate"
                value={temporalStability}
                sx={{
                  mt: 1,
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, 
                      ${temporalStability > 70 ? '#00ff41' : temporalStability > 40 ? '#ff9800' : '#ff0040'}, 
                      #00f5ff
                    )`
                  }
                }}
              />
            </CardContent>
          </Card>

          <Card sx={{ background: 'linear-gradient(135deg, #000814 0%, #001d3d 100%)', border: '1px solid #ff00ff' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ff00ff', mb: 2 }}>
                Message Transmission
              </Typography>

              <TextField
                fullWidth
                value={messageToSend}
                onChange={(e) => setMessageToSend(e.target.value)}
                placeholder="Enter message to send to the past..."
                variant="outlined"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#ff00ff' },
                    '&:hover fieldset': { borderColor: '#ff44ff' },
                    '&.Mui-focused fieldset': { borderColor: '#ff00ff' }
                  },
                  '& .MuiInputLabel-root': { color: '#ff00ff' }
                }}
              />

              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={!messageToSend.trim() || !isActive}
                sx={{
                  background: 'linear-gradient(45deg, #ff00ff, #ff44ff)',
                  color: '#000',
                  fontWeight: 'bold'
                }}
              >
                ENCODE FOR TRANSMISSION
              </Button>

              <Typography variant="body2" sx={{ color: '#aaa', mt: 2 }}>
                Note: In theory, this message would be encoded in quantum states and transmitted via retrocausal correlation
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ background: 'linear-gradient(135deg, #000814 0%, #001d3d 100%)', border: '1px solid #00ff41', mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#00ff41', mb: 2 }}>
                Incoming Bitstream
              </Typography>

              <BitstreamDisplay>
                {formatBitstream() || 'Waiting for quantum data...'}
              </BitstreamDisplay>

              <Typography variant="body2" sx={{ color: '#aaa', mt: 1 }}>
                Live feed from quantum experiment • {receivedBits.length} bits received
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ background: 'linear-gradient(135deg, #000814 0%, #001d3d 100%)', border: '1px solid #ffff00' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ffff00', mb: 2 }}>
                Decoded Messages
              </Typography>

              <Box
                sx={{
                  background: '#000',
                  border: '1px solid #ffff00',
                  borderRadius: 4,
                  padding: 2,
                  minHeight: 100,
                  fontFamily: 'Courier New, monospace',
                  color: '#ffff00',
                  wordBreak: 'break-all'
                }}
              >
                {decodedMessage || 'No coherent messages decoded yet...'}
              </Box>

              {anomalyDetected && (
                <Alert 
                  severity="warning" 
                  sx={{ 
                    mt: 2, 
                    background: 'rgba(255, 87, 34, 0.1)',
                    border: '1px solid #ff5722',
                    '& .MuiAlert-message': { color: '#ff5722' }
                  }}
                >
                  Temporal anomaly detected! High-confidence future signal received.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', border: '1px solid #aaa', mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#aaa', mb: 2 }}>
            Theoretical Framework
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={{ color: '#00ff41' }}>
                Quantum Mechanism
              </Typography>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Delayed-choice quantum eraser experiments show future measurements correlate with past states
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={{ color: '#00f5ff' }}>
                No-Communication Theorem
              </Typography>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Standard QM prevents information transfer, but statistical patterns might encode single bits
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={{ color: '#ff00ff' }}>
                Multiverse Theory
              </Typography>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Many-worlds interpretation suggests branch-to-branch messaging without paradox
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={{ color: '#ffff00' }}>
                Implications
              </Typography>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Even 1 bit of retrocausal information could enable massive cascade effects
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};